export interface CycleRequiredError {
  code: "cycle_required";
  message: string;
  availableCycles: number[];
}

interface ApiErrorLike {
  response?: {
    status?: number;
    data?: any;
  };
}

export function isCycleRequiredError(error: unknown): CycleRequiredError | null {
  const response = (error as ApiErrorLike)?.response;
  if (response?.status === 409 && response.data?.code === "cycle_required") {
    return response.data as CycleRequiredError;
  }
  return null;
}

export function isCycleNotFoundError(error: unknown): boolean {
  return (error as ApiErrorLike)?.response?.status === 404;
}

export interface CycleAlreadyGeneratedError {
  code: "cycle_already_generated";
  message: string;
  sourceYear: number;
  targetYear: number;
  completedRunId: number;
}

export function isCycleAlreadyGeneratedError(error: unknown): CycleAlreadyGeneratedError | null {
  const response = (error as ApiErrorLike)?.response;
  if (response?.status === 409 && response.data?.code === "cycle_already_generated") {
    return response.data as CycleAlreadyGeneratedError;
  }
  return null;
}

/**
 * Erros de `POST /admin/cycles/rollover-schedules`. Ver .claude/docs/frontend_commercial_cycle_rollover_scheduling.md.
 */

export interface RolloverAlreadyScheduledError {
  code: "rollover_already_scheduled";
  message: string;
  sourceYear: number;
  targetYear: number;
  pendingScheduleId: number;
}

export function isRolloverAlreadyScheduledError(error: unknown): RolloverAlreadyScheduledError | null {
  const response = (error as ApiErrorLike)?.response;
  if (response?.status === 409 && response.data?.code === "rollover_already_scheduled") {
    return response.data as RolloverAlreadyScheduledError;
  }
  return null;
}

/** 409 genérico de agendamento (data no passado, ou ciclo destino não está em Draft) — sem `code`. */
export function isRolloverScheduleConflictError(error: unknown): { message: string } | null {
  const response = (error as ApiErrorLike)?.response;
  if (
    response?.status === 409 &&
    typeof response.data?.message === "string" &&
    response.data?.code !== "cycle_already_generated" &&
    response.data?.code !== "rollover_already_scheduled"
  ) {
    return { message: response.data.message };
  }
  return null;
}

export function isRolloverScheduleNotFoundError(error: unknown): boolean {
  return (error as ApiErrorLike)?.response?.status === 404;
}

/**
 * Erros específicos de `POST /orders/create-order` introduzidos pela validação de
 * `CycleCode` (ano + tipo) contra o `CommercialCycle`. Ver .claude/docs/frontend_order_cycle_code.md.
 */

export interface InvalidCycleCodeFormatError {
  field: string;
  message: string;
}

export function isInvalidCycleCodeFormatError(error: unknown): InvalidCycleCodeFormatError | null {
  const response = (error as ApiErrorLike)?.response;
  if (response?.status !== 400) return null;
  const errors = response.data?.errors;
  if (!Array.isArray(errors)) return null;
  const cycleCodeError = errors.find((e) => e?.field === "CycleCode");
  return cycleCodeError ?? null;
}

export function isCycleNotConfiguredError(error: unknown): boolean {
  const response = (error as ApiErrorLike)?.response;
  return response?.status === 400 && typeof response.data?.message === "string" && !Array.isArray(response.data?.errors);
}

export function isCycleNotOpenError(error: unknown): boolean {
  const response = (error as ApiErrorLike)?.response;
  return (
    response?.status === 409 &&
    typeof response.data?.message === "string" &&
    response.data?.code !== "cycle_required" &&
    response.data?.code !== "cycle_already_generated"
  );
}
